import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostBinding, Injector, Input, AfterViewInit, OnDestroy } from '@angular/core';
import { DestroyGuard } from 'services/browser-utils';
import { getReadableAppVersion } from 'environments/version-getters';

@Component({
  selector: 'cs-version-display',
  templateUrl: 'cs-version-display.html',
  host: { 'class': 'version-display' },
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CsVersionDisplayComponent implements AfterViewInit, OnDestroy {
  @Input() scrollSelector = '.scroll-content';
  @HostBinding('class.visible') isVisible = false;

  version = getReadableAppVersion();

  private scrollHandler: (() => void) | null = null;
  private resizeHandler: (() => void) | null = null;
  private destroyGuard = new DestroyGuard();

  constructor(
    private elementRef: ElementRef,
    private injector: Injector,
    private cdr: ChangeDetectorRef,
  ) {}

  async ngAfterViewInit() {
    // the wait now returns when this component is destroyed instead of hanging
    // (df-3f0eaff27ac6), so the listeners below have to be skipped in that case:
    // ngOnDestroy has already run, and the window resize listener it removes would
    // then stay attached to the window for the rest of the session
    if (!await this.destroyGuard.safeWaitForAngularUpdate(this.injector)) return;
    this.setupScrollListener();
  }

  ngOnDestroy() {
    this.destroyGuard.destroy();
    this.removeScrollListener();
  }

  private setupScrollListener() {
    // find scroll container by traversing up to find the element matching scrollSelector
    const scrollContent = this.findScrollContainer();
    if (!scrollContent) return;

    this.scrollHandler = () => {
      this.checkScrollPosition(scrollContent);
    };

    this.resizeHandler = () => {
      this.checkScrollPosition(scrollContent);
    };

    scrollContent.addEventListener('scroll', this.scrollHandler);
    window.addEventListener('resize', this.resizeHandler);
    // check initial state
    this.checkScrollPosition(scrollContent);
  }

  private findScrollContainer(): HTMLElement | null {
    // traverse up from this element to find scroll container
    let element: HTMLElement | null = this.elementRef.nativeElement;
    while (element) {
      const parent = element.parentElement;
      if (!parent) break;

      const scrollContainer = parent.querySelector(this.scrollSelector);
      if (scrollContainer) {
        return scrollContainer as HTMLElement;
      }
      element = parent;
    }
    return null;
  }

  private removeScrollListener() {
    if (this.scrollHandler) {
      const scrollContent = this.findScrollContainer();
      if (scrollContent) {
        scrollContent.removeEventListener('scroll', this.scrollHandler);
      }
      this.scrollHandler = null;
    }
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
      this.resizeHandler = null;
    }
  }

  private checkScrollPosition(scrollContent: HTMLElement) {
    const { scrollTop, scrollHeight, clientHeight } = scrollContent;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 10;
    const hasNoScroll = scrollHeight <= clientHeight;

    const shouldShow = isAtBottom || hasNoScroll;
    if (this.isVisible !== shouldShow) {
      this.isVisible = shouldShow;
      this.cdr.markForCheck();
    }
  }
}
